import { ReactNode } from "react";
import { Session } from "@chromia/ft4";
import { IClient, Web3PromiEvent } from "postchain-client";
import BN from "bn.js";
import { TransactionWithReceipt } from "@chromia/ft4/dist/types/transaction-builder";

import { Deposit } from "./classes/Deposit";
import { blockchain, discountPercents } from "./settings";
import { ChrDeposit } from "./classes/ChrDeposit";
import { BigNumber } from "./classes/BigNumber";

export interface iReduxState {
	app: iAppState,
	modals: iModalsState,
	UI: iUIState,
	chromia: iChromiaState,
}

export type tAddress = `0x${string}`;

export type tDiscountPercent = typeof discountPercents[number];

export interface iAppState {
	discountPercent: tDiscountPercent,
	userAddress: tAddress | null,
	apiData: {[key in tDiscountPercent]: iApiData | null},
	appContracts: {[key in tDiscountPercent]: iAppContract | null},
	fTokens: {[key in tDiscountPercent]: iToken | null},
	uTokens: {[key in tDiscountPercent]: iToken | null},
	currentPeriods: {[key in tDiscountPercent]: iPeriodResponse | null},
	currentTimes: {[key in tDiscountPercent]: iTime | null},
	deposits: {[key in tDiscountPercent]: Deposit[]},
	isInited: boolean,
	minDeposits: {[key in tDiscountPercent]: BigNumber | null},
	kasAccessToken: string | null,
}

export interface iModalsState {
	modalsIds: {[K in tModalName] :number},
	openedModals: (/* iSimpleModal | */ iInfoModal |iConfirmModal | iTransactionModal)[],
}

export interface iUIState {
	isDepositAvailable: {[key in tDiscountPercent]: boolean},	// endTime sub 24hours ?
	// isWithdrawAvailable: {[key in tDiscountPercent]: boolean},
}

export interface iApiData {
	api: string,
	config: iApiConfig
}

export interface iApiConfig {
	/* net: {
		chainID: string,
		httpProvider: string
	}, */
	sbd: {
		/**
		 * Contract abi.
		 */
		abi: string,
		/**
		 * Contract address.
		 */
		adr: tAddress,
		/**
		 * Contract end time in seconds. Timestamp.
		 */
		end_time: number,
		in_work: 0 | 1,
		/**
		 * Number. 10*18.
		 */
		strike_mult: string
	},
	tokens: {
		f_tok: {
			adr: tAddress,
			dec: number,
			name: string,
		}
		tok_abi: string,
		u_tok: {
			adr: tAddress,
			dec: number,
			name: string,
		}
	}
}

export interface iToken {
	address: tAddress
	name: string,
	decimals: number,
	contract: iTokenContract
}

export type tModalName = 'info' | 'confirm' | 'transaction';

/* export interface iSimpleModal {
	name: Extract<tModalName, 'transactionSettings' | 'LSTPro' | 'mobileMenu'>,
	modalId: number,
} */

export interface iInfoModal {
	name: Extract<tModalName, 'info'>,
	modalId: number,
	/**
	 * You can use HTML.
	 * 
	 * For next line in plain text use '\n'.
	*/
	message?: ReactNode,
	title?: ReactNode,
	type?: 'success' | 'warning',
}

export interface iTransactionModal {
	name: Extract<tModalName, 'transaction'>,
	modalId: number,
	transactionParams:
		{blockchain: Extract<typeof blockchain, 'binance'>, transaction: iPromiEvent<iTransactionReceipt>} |
		// {blockchain: Extract<typeof blockchain, 'chromia'>, transaction: Promise<any>}
		{blockchain: Extract<typeof blockchain, 'chromia'>, transaction: Web3PromiEvent<TransactionWithReceipt, {built: Buffer;sent: Buffer;}>}
}

export interface iConfirmModal {
	name: Extract<tModalName, 'confirm'>,
	modalId: number,
	promiseFork: iPromiseFork1<any>,	// type 'any' because we don't need this value.
	title?: ReactNode,
	message?: ReactNode,
	cancelButton?: {
		text?: ReactNode,
		action?: Function,
	}
	confirmButton?: {
		text?: ReactNode,
		action?: Function,
	}
}

export interface iPromiseFork2<resolveT, rejectT> {
	resolve: null | ( (value: resolveT) => void ) ,
	reject: null | ( (value: rejectT) => void ) ,
}
export interface iPromiseFork1<T> {
	resolve: null | ( (value: T) => void ) ,
	reject: null | ( (value: T) => void ) ,
}

export interface iDepositResponse {
	c: string,	// number 10**18
	can_withdraw: '0' | '1',	// bool
	client: tAddress,	// not used
	close_c: string,	// number 10**18
	depo_amt: string,	// number 10**18
	end_time: string,	// number?
	finished: boolean,
	ft_amt: string,	// number?,	not used
	id: number,
	l_ft_amt: string,	// number 10**18
	l_ut_amt: string,	// number 10**18
	l_ut_price: string,	// number 10**18
	period: number,	// not used
	ut_amt: string,	// number?, not used
}

export interface iAppContract {
	methods: {
		Deposit: (value: BN) => iSendMethod,
		Withdraw: (id: number) => iSendMethod,
		/* NewPeriod: (strikePrice: BN, ordRefPrice: BN, ordDelta: BN, ordPrice: BN, ordMinPrice: BN) => {
			encodeABI: () => void
		},
		EndPeriod: () => {
			encodeABI: () => void
		}, */
		NewPeriod: (strikePrice: BN, ordRefPrice: BN, ordDelta: BN, ordPrice: BN, ordMinPrice: BN) => iSendMethod,
		EndPeriod: () => iSendMethod,
		now_time: () => { call: () => Promise<string> },
		set_time: (ts: number) => iSendMethod,
		EOptApproveFt: (amount: BN) => iSendMethod,
	}
}

export interface iTokenContract {
	methods: {
		allowance: (userAddress: tAddress, appContractAddr: string) => { call: () => Promise<string> },
		balanceOf: (userAddress: tAddress) => { call: () => Promise<string> },
		approve: (appContractAddress: string, amount: BN) => iSendMethod,
		totalSupply: () => { call: () => Promise<number> },
	}
}

export interface iPeriodResponse {
	num: number,
	launch_num: number,
	ft_b: string,	// number 10**18
	ft_e: string,	// number 10**18?
	ut_b: string,	// number 10**18?
	ut_e: string,	// number 10**18?
	c_b: string,	// number 10**18
	c_e: string,	// number 10**18?
	end_time: number,	// ts in secs
	opt_id: number,
	created_amt: string,	// number 10**18
	ord_id: number,
	finished: boolean,
}

export interface iTime {
	ts: number,
	formatted: string,
}

interface iSendMethod {
	send: (options: {
		from: string,
		gasPrice?: string,  // The gas price in wei to use for this transaction
		gas?: number,   // The maximum gas provided for this transaction (gas limit)
	}) => iPromiEvent<iTransactionReceipt>
}

export interface iPromiEvent<T> extends Promise<T> {
	once(
		type: 'sending',
		handler: (payload: iTransactionPayload) => void
	): iPromiEvent<T>;

	once(
		type: 'sent',
		handler: (payload: iTransactionPayload) => void
	): iPromiEvent<T>;

	once(
		type: 'transactionHash',
		handler: (transactionHash: string) => void
	): iPromiEvent<T>;

	once(
		type: 'receipt',
		handler: (receipt: iTransactionReceipt) => void
	): iPromiEvent<T>;

	once(
		type: 'confirmation',
		handler: (confirmationNumber: number, receipt: iTransactionReceipt, latestBlockHash?: string) => void
	): iPromiEvent<T>;

	once(type: 'error', handler: (error: iTransactionError) => void): iPromiEvent<T>;

	/* once(
		type: 'error' | 'confirmation' | 'receipt' | 'transactionHash' | 'sent' | 'sending',
		handler: (error: iTransactionError | iTransactionReceipt | string | object) => void
	): iPromiEvent<T>; */

	on(
		type: 'sending',
		handler: (payload: iTransactionPayload) => void
	): iPromiEvent<T>;

	on(
		type: 'sent',
		handler: (payload: iTransactionPayload) => void
	): iPromiEvent<T>;

	on(
		type: 'transactionHash',
		handler: (receipt: string) => void
	): iPromiEvent<T>;

	on(
		type: 'receipt',
		handler: (receipt: iTransactionReceipt) => void
	): iPromiEvent<T>;

	on(
		type: 'confirmation',
		handler: (confNumber: number, receipt: iTransactionReceipt, latestBlockHash?: string) => void
	): iPromiEvent<T>;

	on(type: 'error', handler: (error: iTransactionError) => void): iPromiEvent<T>;

	/* on(
		type: 'error' | 'confirmation' | 'receipt' | 'transactionHash' | 'sent' | 'sending',
		handler: (error: iTransactionError | iTransactionReceipt | string | object) => void
	): iPromiEvent<T>; */
}
export interface iTransactionReceipt {
	transactionHash: string,
	transactionIndex: number,
	blockHash: string,
	blockNumber: number,
	contractAddress?: string,
	cumulativeGasUsed: number,
	gasUsed: number,
	events: iTransactionEvent[]
	effectiveGasPrice: number,	// price (e.g. 10) * 10**9
	status: boolean;
	from: string;
	to: string;
	// logs: Log[];
	// logsBloom: string;
}
/* export interface Log {
	address: string;
	data: string;
	topics: string[];
	logIndex: number;
	transactionIndex: number;
	transactionHash: string;
	blockHash: string;
	blockNumber: number;
} */
export interface iTransactionPayload {
	// callback
	method: string, // eth_sendTransaction
	params: {
		data: string,   // undefined -?
		from: string,
		gas: undefined, // string - ?
		gasPrice: string,	// undefined - ?
		to: string,
	}[]
}
export interface iTransactionError {
	/**
	 * 4001 - User denied transaction signature.
	 */
	code: number,
	message: string,
	stack: string,
}
export interface iTransactionEvent {
	address: string,
	blockHash: string,
	blockNumber: number,
	event: undefined,	// ???
	id: string,
	logIndex: number,
	raw: {
		data: string,
		topics: string[],
	}
	removed: boolean,
	// returnValues: u {},	// ???
	signature: null,	// ??
	transactionHash: string,
	transactionIndex: number,
}

export const ETHval = new BN('1000000000000000000');	// 10**18


// chromia
export interface iChromiaState {
	session: Session | null,
	chrClientAddr: string | null,
	gtxClient: iTypedGtxClient | null,
	deposits: {[key in tDiscountPercent]: ChrDeposit[]},
	launches: iChrLaunch[],		// unused
	periods: iChrPeriod[],		// unused
	isChrRegistered: boolean,
	items: {[key in tDiscountPercent]: iChrItem | null},
	endTimes: {[key in tDiscountPercent]: number},
}

export interface iChrQueries {
	"ft4.get_all_assets": {
		params: {
			page_size: number;	// 10
			page_cursor: string | null;	// null
		};
		return: {
			data: iChrAsset[]
		};
	},
	"ft4.get_account_auth_descriptors": {
		params: {
			id: string;
			page_size: number;
			page_cursor: string | null;
		};
		return: { data: { args: [string[], Buffer]; rules: unknown; type: string }[] };
	},
	"ft4.get_asset_balances": {
		params: {
			account_id: string,
			page_size: number,
			page_cursor: string | null,
		};
		return: {
			data: {
				amount: BigInt,
				asset: iChrAsset,
			}[],
			next_cursor: string | null,
		};
	},
	"ft4.get_asset_balance": {
		params: {
			account_id: string,
			asset_id: string,
		};
		return: {
			amount: BigInt,
			asset: iChrAsset,
		} | null;
	},
	"pickaxe.GetAllPeriods": {
		params: {item_id: string},
		return: iChrPeriod[]
	},
	"pickaxe.GetAllLaunchs": {
		params: {item_id: string},
		return: iChrLaunch[]
	},
	"pickaxe.GetAllDepos": {
		params: {item_id: string},
		return: iChrDeposit[]
	},
	/* "pickaxe.GetPeriod": {
		params: {
			item_id: string,
			id: number,	// period id
		},
		return: {}
	}, */
	/* "pickaxe.GetLaunch": {
		params: {
			item_id: string,
			id: number,	// launch id
		},
		return: {}
	}, */
	/* "pickaxe.GetLaunchByPeriodId": {
		params: {
			per_id: number
		},
		return: {launch: iChrLaunch}
	}, */
	"pickaxe.GetClientDepos": {
		params: {
			item_id: string,
			/**
			 * Chr address.
			 */
			from: string,
		},
		return: iChrDeposit[]
	},
	"pickaxe.GetItems": {
		params: {},
		return: iChrItemResponse[]
	},
	"pickaxe.GetItem": {
		params: {item_id: string},
		return: iChrItemResponse
	}
	"pickaxe.GetTime": {
		params: {},
		/**
		 * ts
		 */
		return: number
	},
	"pickaxe.GetState": {
		params: {item_id: string},
		return: {
			/**
			 * ts
			 */
			end_time: number,
			/**
			 * Human-readable(?) number.
			 */
			ft_clients: string,
			/**
			 * Human-readable(?) number.
			 */
			ft_depo_amt: string,
			/**
			 * Human-readable(?) number.
			 */
			ft_per_b: string,
			in_work: 0 | 1,
			inited: 0 | 1,
			launch_num: number,
			/**
			 * ts?
			 */
			opt_end_time: number,
			opt_id: Buffer,
			/**
			 * Human-readable(?) number.
			 */
			opt_strike: string,
			ord_id: number,
			period_num: number,
			/**
			 * ?
			 */
			started: 0 | 1,
			/**
			 * Human-readable(?) number.
			 */
			ut_clients: string,
			/**
			 * Human-readable(?) number.
			 */
			ut_per_b: string,
		}
	},
	"pickaxe.GetInWork": {
		params: {item_id: string},
		return: 0 | 1,
	},
	"slurpyswap.GetTime": {
		params: {},
		return: number	// ts, dev in ms
	},
	"hedget.GetTime": {
		params: {},
		return: number	// ts
	}
}

export interface iChrDeposit {
	from: Buffer,
	/**
	 * Human-readable (?) number.
	 */
	ft_amt: bigint,
	ft_amt_dec: string,
	item: Buffer,
	per_id: number,
}

export interface iChrLaunch {
	/**
	 * Human-readable (?) number
	*/
	close_c: string,
	/**
	 * Human-readable (?) number
	*/
	ft_amt: string,
	id: number,
	/**
	 * 1 - started
	 * 
	 * 2 - finished
	*/
	state: 1 | 2,
	/**
	 * Human-readable (?) number
	*/
	ut_amt: string,
	/**
	 * Human-readable (?) number
	*/
	ut_price: string,
}

export interface iChrPeriod {
	id: number,
	launch_id: number,
	/**
	 * Human-readable (?) number
	 */
	start_c: string,
	/**
	 * 1 - started
	 * 
	 * 2 - finished
	*/
	state: 1 | 2,
}

/* export enum eChrOperations {

} */

export interface iChrAsset {
	brid: Buffer,
	decimals: number,
	icon_url: string,
	id: Buffer,
	name: string,
	supply: BigInt,
	symbol: string
}

export interface iTypedGtxClient extends Omit<IClient, "query" | "newTransaction"> {
	query<T extends keyof iChrQueries>(
		name: T,
		params: iChrQueries[T]["params"]
		// params?: Queries[T]["params"]
	): Promise<iChrQueries[T]["return"]>;
}

export interface iChrToken {
	currency: string,
	address: string,
	// id?: Buffer,
	dec: number,
	show_dec: number,
	// contract: iToken_contract,
	// type: 'funding' | 'underlying' | 'commission',
}

export interface iChrItemResponse {
	acc: Buffer,
	/**
	 * Human-readable number
	 */
	discount_ut: string,
	/**
	 * auto exercise
	 */
	eopt_ae_time: number,
	ft: Buffer,
	/**
	 * Human-readable number
	 */
	min_depo: string,
	owner: Buffer,
	/**
	 * Periods (launches in fact) count.
	 */
	per_count: number,
	/**
	 * In seconds.
	 */
	per_start_time: number,
	/**
	 * In seconds.
	 */
	per_time: number,
	price_pool: Buffer,
	ut: Buffer,
}

export interface iChrItem {
	/**
	 * Item id.
	 */
	acc: string,
	discount_ut: BigNumber,
	/**
	 * auto exercise
	 */
	eopt_ae_time: number,
	ft: string,
	min_depo: BigNumber,
	owner: string,
	/**
	 * Periods count.
	 */
	per_count: number,
	/**
	 * In seconds.
	 */
	per_start_time: number,
	/**
	 * In seconds.
	 */
	per_time: number,
	price_pool: string,
	ut: string,
	// end_time: number,
}

export interface iChrError {
	name: string,
	originalError: {
		code: string,
		action: string,
		reason: string,
		info: {
			error: {
				/**
				 * 4001 - user rejected action
				 */
				code: number,
				message: string
			},
			payload: {
				id: number,
				jsonrpc: string,
				method: string,
				params: string[]
			},
		},
		shortMessage: string
	}
}

export interface iChrStateResponse {
	end_time: 1726387200,
	ft_clients: 10n,
	ft_clients_dec: "0.00000000000000001",
	ft_depo_amt: 1423000000000000000000n,
	ft_depo_amt_dec: "1423",
	ft_per_b: 2036000563782041190397n,
	ft_per_b_dec: "2036.000563782041190397",
	in_work: 1,
	inited: 1,
	item: Buffer,
	launch_num: 2,
	opt_end_time: 1724314200,
	opt_id: Buffer,
	opt_strike: "53668.31050468451555507063",
	ord_id: 950,
	period_num: 7,
	started: 1,
	ut_clients: 0n,
	ut_clients_dec: "0",
	ut_per_b: 0n,
	ut_per_b_dec: "0",
}